import React from 'react'
import { ApolloClient, gql, useQuery } from '@apollo/client'
import { Switch, Case, Default } from 'react-if'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { LandscapeTileCard } from '@/molecules/LandscapeRowCollection'
import { PlaceholderMediaCard } from '@/molecules/MediaCard'
import { ProgressOverlay } from '@/molecules/MediaCard/ProgressOverlay'
import { RetryFallback } from '@/molecules/RetryFallback'
import { createPlaceholderArray } from '@/utils/PlaceholderArrays'
import { useTranslate } from '@/utils/translate/translate-client'

const loadingTiles = createPlaceholderArray(4)
const PLACEHOLDER_CHILDREN = loadingTiles.map((tile) => <PlaceholderMediaCard key={tile} />)

export interface VideoThumbnailMeta {
  guid: string
  title: string
  subtitle: string
  posterLandscapeCloudinaryPath: string
  watchPosition?: {
    position: number
  }
  source: {
    duration: number
  }
  page: string
}

export interface VideoGridProps {
  projectSlug: string
  page: string
  excludeGuid?: string
  client?: ApolloClient<object>
}

export const getVideoQuery = gql`
  query getVideosByPageForProject($projectSlug: String!, $page: String) {
    project(slug: $projectSlug) {
      videos(page: $page) {
        guid
        title
        subtitle
        posterLandscapeCloudinaryPath
        watchPosition {
          position
        }
        source {
          duration
        }
        page
      }
    }
  }
`

export const VideoGrid: React.FC<VideoGridProps> = ({ projectSlug, page, excludeGuid }) => {
  const { data, loading, refetch, error } = useQuery(getVideoQuery, {
    variables: { projectSlug, page },
    errorPolicy: 'all',
  })

  const { t } = useTranslate('watch')

  const videos = data?.project?.videos ? [...data.project.videos] : []
  const filteredVideos = videos.filter(
    (video: VideoThumbnailMeta) => video.guid !== excludeGuid,
  ) as VideoThumbnailMeta[]

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
      <Switch>
        <Case condition={loading}>{PLACEHOLDER_CHILDREN}</Case>
        <Case condition={!!error}>
          <RetryFallback
            className="py-20 text-center sm:col-span-2 md:col-span-3 lg:col-span-2 lg:col-start-2"
            message={t('problemLoadingVideos', 'Oops! There was a problem loading videos.')}
            ctaAction={() => refetch?.()}
            ctaText={t('retry', 'Retry')}
          />
        </Case>
        <Case condition={filteredVideos?.length > 0}>
          {filteredVideos?.map((video) => {
            let progress = 0

            if (video?.watchPosition?.position && video.source?.duration) {
              progress = (video?.watchPosition?.position / video?.source.duration) * 100
            }

            return (
              <LandscapeTileCard
                className="mr-4"
                key={video.guid}
                linkModel={{
                  linkUrl: `/watch/${projectSlug}/videos/${video.page}/${video.guid}`,
                  alt: video.title,
                  imageUrl: video.posterLandscapeCloudinaryPath,
                  label: video.title,
                }}
              >
                <PlayButtonThumbnailOverlay />
                <ProgressOverlay progress={progress} />
              </LandscapeTileCard>
            )
          })}
        </Case>
        <Case condition={videos.length === 1 && filteredVideos?.length === 0} />
        <Default>
          <div className="py-20 text-center sm:col-span-2 md:col-span-3 lg:col-span-2 lg:col-start-2">
            {t('noContentAvailable', 'There is no content available.')}
          </div>
        </Default>
      </Switch>
    </div>
  )
}
